import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  NOMBRE_TIPO,
  totalDeFactura,
  type ArchivoSubido,
  type EstadoSubirTodo,
  type ExpenseCategory,
  type Falta,
  type ItemGasto,
  type ItemPlato,
  type TipoDocumento,
} from '@alexiarest/shared';
import { AlexiaAvatar, useEstadoAlexia } from '../components/AlexiaAvatar';
import { SmartSelect } from '../components/Field';
import { ImportCatalogWizard } from '../components/ImportCatalogWizard';
import { ImportExpensesWizard } from '../components/ImportExpensesWizard';
import { ImportSalesWizard } from '../components/ImportSalesWizard';
import { PosIntegrationsCard } from '../components/PosIntegrations';
import { Drawer } from '../components/Drawer';
import { TellAlexiaRecipeDrawer } from '../components/TellAlexiaRecipeDrawer';
import { whatsappCon } from '../components/SupportButton';
import { useToast } from '../components/Toast';
import { useLocationScope } from '../context/LocationContext';
import { useMe } from '../api/hooks';
import { formatDate, formatMoney, parseDecimal } from '../format';
import { ACCEPT_ESCANER, prepararImagen } from '../imagenEscaner';
import {
  useDecirTipo,
  useEditarItem,
  useEmpezarSubirTodo,
  useGuardarGrupo,
  useReintentar,
  useSubirArchivo,
  useSubirTodo,
} from '../components/subirTodo/useSubirTodo';
import '../components/subirTodo/subir-todo.css';

/*
 * Onboarding «Súbelo todo» (29 oct 2026). Lo primero que ve el dueño: Alexia
 * le pide todo lo que tenga de su restaurante, lo lee en segundo plano, se lo
 * enseña agrupado para que lo confirme y le dice qué le falta, con un botón
 * para cada hueco. Ver docs/product/onboarding-subir-todo.md.
 *
 * Desde el sofá: en el móvil, «Hacer fotos» abre la cámara y se pueden hacer
 * varias seguidas; cada una entra en la lista y se lee sola.
 */

const OPCIONES_IMAGEN = {
  maxLado: 2000,
  maxPdfBytes: 15_000_000,
  errorPdf: 'Ese PDF pesa demasiado (máximo 15 MB). Exporta solo las páginas que importan.',
  errorTamanyo: 'La imagen es demasiado grande. Prueba a hacer la foto más de cerca.',
};

const esExcel = (file: File) => /\.(xlsx|xls|csv)$/i.test(file.name) || /spreadsheet|excel|csv/.test(file.type);

const TIPOS_PREGUNTA: Array<{ tipo: TipoDocumento; label: string }> = [
  { tipo: 'factura', label: 'Factura' },
  { tipo: 'albaran', label: 'Albarán' },
  { tipo: 'carta', label: 'Carta' },
  { tipo: 'nomina', label: 'Nómina' },
  { tipo: 'otro', label: 'Otra cosa' },
];

type Lateral = 'tpv' | 'ventas' | 'unidades' | 'gastos' | 'recetas-excel' | 'receta';

export function SubirTodoPage(): JSX.Element {
  const consulta = useSubirTodo();
  const empezar = useEmpezarSubirTodo();
  const arrancado = useRef(false);

  // Entrar es empezar: sin subida en curso, se abre una sin pedir nada.
  useEffect(() => {
    if (arrancado.current || consulta.isLoading || consulta.data?.job) return;
    arrancado.current = true;
    empezar.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consulta.isLoading, consulta.data?.job]);

  const estado = consulta.data && consulta.data.job ? (consulta.data as EstadoSubirTodo) : undefined;
  if (consulta.isError || empezar.isError) {
    return <div className="st"><p className="st-error" role="alert">{(consulta.error ?? empezar.error)?.message ?? 'No he podido abrir la subida.'}</p></div>;
  }
  if (!estado) return <div className="st"><p className="st-cargando" role="status">Preparando tu puesta en marcha…</p></div>;
  return <Pantalla estado={estado} />;
}

function Pantalla({ estado }: { estado: EstadoSubirTodo }): JSX.Element {
  const [lateral, setLateral] = useState<Lateral | null>(null);
  const [platoParaReceta, setPlatoParaReceta] = useState<string | undefined>();
  const navigate = useNavigate();
  const { faltas } = estado;
  const gastos = estado.items.filter((i): i is ItemGasto & { dudas: string[] } => i.kind === 'gasto');
  const platos = estado.items.filter((i): i is ItemPlato & { dudas: string[] } => i.kind === 'plato');
  const nada = estado.archivos.length === 0;
  // Es Alexia quien pide y quien lee: piensa mientras hay archivos en marcha.
  const caraDeAlexia = useEstadoAlexia(estado.archivos.some((a) => a.estado === 'en_cola' || a.estado === 'leyendo'), estado.archivos.some((a) => a.estado === 'error' || a.estado === 'no_se_lee'));

  const accion = (f: Pick<Falta, 'accion' | 'afectados'>['accion'], afectados?: string[]) => {
    switch (f.tipo) {
      case 'subir': document.getElementById('st-subir')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); break;
      case 'conectar-tpv': setLateral('tpv'); break;
      case 'importar-ventas': setLateral('ventas'); break;
      case 'importar-unidades': setLateral('unidades'); break;
      case 'importar-gastos': setLateral('gastos'); break;
      case 'receta': setPlatoParaReceta(afectados?.[0]); setLateral('receta'); break;
      case 'ver-resultados': navigate('/resultados'); break;
    }
  };

  return (
    <div className="st">
      <header className="st-alexia">
        <span className="st-alexia__avatar"><AlexiaAvatar estado={caraDeAlexia} /></span>
        <div className="st-alexia__burbuja">
          {nada ? (
            <>
              <h1 className="st-alexia__titulo">Dame todo lo que tengas de tu restaurante</h1>
              <p>Facturas, la carta, el Excel de ventas, lo que te pasa la gestoría… Fotos o PDFs, como lo tengas. Yo lo leo, te lo ordeno y te digo qué me falta.</p>
            </>
          ) : (
            <>
              <h1 className="st-alexia__titulo">{faltas.hito1.cumplido ? 'Ya puedo enseñarte tus números' : 'Voy leyendo lo que me pasas'}</h1>
              <p>{frase(estado)}</p>
            </>
          )}
        </div>
      </header>

      <Barra estado={estado} />

      <ZonaSubida estado={estado} onExcel={() => setLateral('gastos')} />

      {estado.archivos.length > 0 && <ListaArchivos estado={estado} onAbrir={(l) => setLateral(l)} />}

      {gastos.length > 0 && <GrupoGastos jobId={estado.job.jobId} items={gastos} hito1={faltas.hito1.cumplido} />}
      {platos.length > 0 && <GrupoCarta jobId={estado.job.jobId} items={platos} />}

      <LoQueFalta estado={estado} onAccion={accion} />

      {lateral === 'tpv' && <LateralTpv onClose={() => setLateral(null)} />}
      <ImportSalesWizard mode="ventas" open={lateral === 'ventas'} onClose={() => setLateral(null)} />
      <ImportSalesWizard mode="unidades" open={lateral === 'unidades'} onClose={() => setLateral(null)} />
      <ImportExpensesWizard open={lateral === 'gastos'} onClose={() => setLateral(null)} />
      <ImportCatalogWizard open={lateral === 'recetas-excel'} onClose={() => setLateral(null)} modoInicial="recetas" />
      {lateral === 'receta' && (
        <TellAlexiaRecipeDrawer open requireIngredients initialMode="dictate" initialText={platoParaReceta} onClose={() => setLateral(null)} onSaved={() => setLateral(null)} />
      )}
    </div>
  );
}

function frase(estado: EstadoSubirTodo): string {
  const enCurso = estado.archivos.filter((a) => a.estado === 'en_cola' || a.estado === 'leyendo').length;
  if (enCurso) return `Estoy leyendo ${enCurso === 1 ? 'un archivo' : `${enCurso} archivos`}. Puedes seguir subiendo o cerrar: sigo yo y lo encontrarás aquí.`;
  const porConfirmar = estado.items.filter((i) => i.estado === 'propuesto' && i.dudas.length > 0).length;
  const porGuardar = estado.items.filter((i) => i.estado === 'propuesto').length;
  if (porConfirmar) return `Revisa ${porConfirmar === 1 ? 'una cosa que no veo clara' : `${porConfirmar} cosas que no veo claras`} y guarda lo demás.`;
  if (porGuardar) return 'Está todo claro. Guárdalo y te digo qué me falta.';
  if (estado.faltas.hito1.cumplido) return 'Con lo que me has pasado ya tienes tu cuenta de resultados.';
  return 'Mira abajo lo que me falta: cada cosa tiene su botón.';
}

/* ------------------------------------------------------------------ */
/* Barra en dos tramos                                                  */
/* ------------------------------------------------------------------ */

function Barra({ estado }: { estado: EstadoSubirTodo }): JSX.Element {
  const { progreso, progresoGuardado } = estado.faltas;
  const pct = (n: number) => `${Math.round(n * 100)}%`;
  return (
    <section className="st-barra" aria-label="Lo que tengo de tu restaurante">
      {([
        ['Tus números', 'Ventas y gastos: tu cuenta de resultados', progresoGuardado.numeros, progreso.numeros],
        ['Tus costes', 'Carta, recetas y precios: cuánto te deja cada plato', progresoGuardado.costes, progreso.costes],
      ] as const).map(([titulo, sub, guardado, leido]) => (
        <div key={titulo} className="st-barra__tramo">
          <div className="st-barra__cabecera"><strong>{titulo}</strong><span>{pct(leido)}</span></div>
          <div className="st-barra__pista" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(leido * 100)} aria-label={titulo}>
            <div className="st-barra__leido" style={{ width: pct(leido) }} />
            <div className="st-barra__guardado" style={{ width: pct(guardado) }} />
          </div>
          <small>{sub}{leido > guardado ? ' · lo rayado está leído pero sin guardar' : ''}</small>
        </div>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Subir                                                                */
/* ------------------------------------------------------------------ */

function ZonaSubida({ estado, onExcel }: { estado: EstadoSubirTodo; onExcel: () => void }): JSX.Element {
  const subir = useSubirArchivo();
  const toast = useToast();
  const { locations } = useLocationScope();
  const activos = locations.filter((l) => l.active);
  const [local, setLocal] = useState<string>('');
  const [arrastrando, setArrastrando] = useState(false);
  const [excels, setExcels] = useState<string[]>([]);
  const fotos = useRef<HTMLInputElement>(null);
  const archivos = useRef<HTMLInputElement>(null);

  const recibir = async (lista: FileList | File[] | null) => {
    const files = [...(lista ?? [])];
    if (!files.length) return;
    const hojas = files.filter(esExcel);
    if (hojas.length) setExcels((prev) => [...prev, ...hojas.map((f) => f.name)]);
    // De una en una: el móvil no se ahoga subiendo diez fotos a la vez.
    for (const file of files.filter((f) => !esExcel(f))) {
      try {
        const blob = await prepararImagen(file, OPCIONES_IMAGEN);
        await subir.mutateAsync({ jobId: estado.job.jobId, file: blob, nombre: file.name, ...(local ? { locationId: local } : {}) });
      } catch (error) {
        toast.fail(error);
      }
    }
  };

  return (
    <section
      id="st-subir"
      className={`st-subir${arrastrando ? ' is-encima' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => { e.preventDefault(); setArrastrando(false); void recibir(e.dataTransfer.files); }}
    >
      <p className="st-subir__texto">Arrastra aquí tus archivos o elige cómo subirlos</p>
      <div className="st-subir__botones">
        <button type="button" className="btn btn--primary st-solo-movil" onClick={() => fotos.current?.click()} disabled={subir.isPending}>Hacer fotos</button>
        <button type="button" className="btn btn--primary" onClick={() => archivos.current?.click()} disabled={subir.isPending}>{subir.isPending ? 'Subiendo…' : 'Elegir archivos'}</button>
      </div>
      <input ref={fotos} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => { void recibir(e.target.files); e.target.value = ''; }} />
      <input ref={archivos} type="file" accept={`${ACCEPT_ESCANER},.xlsx,.xls,.csv`} multiple hidden onChange={(e) => { void recibir(e.target.files); e.target.value = ''; }} />
      <p className="st-subir__ejemplos">Facturas · Albaranes · La carta · Nóminas · Excel de ventas o de la gestoría</p>
      {activos.length > 1 && (
        <label className="st-subir__local">
          <span>¿De qué local es?</span>
          <SmartSelect value={local} onChange={setLocal} ariaLabel="Local de lo que subes" options={[{ value: '', label: 'Del principal / no lo sé' }, ...activos.map((l) => ({ value: l.locationId, label: l.name }))]} />
        </label>
      )}
      {excels.length > 0 && (
        <div className="st-aviso" role="status">
          <strong>{excels.length === 1 ? `«${excels[0]}» es un Excel.` : `${excels.length} Excels.`}</strong> Los Excels los leo con su asistente, que te enseña las columnas antes de guardar.
          <div className="st-aviso__botones">
            <button type="button" className="btn btn--sm" onClick={onExcel}>Son gastos</button>
            <Link className="btn btn--sm" to="/ingresos">Son ventas</Link>
            <Link className="btn btn--sm" to="/carta">Son recetas</Link>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setExcels([])}>Cerrar</button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Archivos y su estado                                                 */
/* ------------------------------------------------------------------ */

function textoEstado(a: ArchivoSubido): string {
  switch (a.estado) {
    case 'en_cola': return 'En cola…';
    case 'leyendo': return a.tipo ? `Alexia está leyendo tu ${NOMBRE_TIPO[a.tipo]}…` : 'Alexia está mirando qué es…';
    case 'listo': return a.resumen ?? 'Leído';
    case 'preguntar': return '¿Qué es esto? No lo tengo claro.';
    case 'no_se_lee': return a.resumen ?? 'Esto lo cargamos de otra forma.';
    case 'error': return a.error ?? 'No lo he podido leer.';
  }
}

function ListaArchivos({ estado, onAbrir }: { estado: EstadoSubirTodo; onAbrir: (l: Lateral) => void }): JSX.Element {
  const decir = useDecirTipo();
  const reintentar = useReintentar();
  const jobId = estado.job.jobId;
  return (
    <section className="st-bloque" aria-labelledby="st-archivos">
      <h2 id="st-archivos" className="st-h2">Lo que me has pasado <span className="st-cuenta">{estado.archivos.length}</span></h2>
      <ul className="st-archivos">
        {estado.archivos.map((a) => (
          <li key={a.fileId} className={`st-archivo is-${a.estado}`}>
            <span className="st-archivo__icono" aria-hidden="true">{a.estado === 'listo' ? '✓' : a.estado === 'error' ? '!' : a.estado === 'preguntar' ? '?' : a.estado === 'no_se_lee' ? '→' : ''}</span>
            <div className="st-archivo__texto">
              <strong title={a.nombre}>{a.nombre}</strong>
              <span role={a.estado === 'leyendo' ? 'status' : undefined}>{textoEstado(a)}</span>
              {a.estado === 'preguntar' && (
                <div className="st-chips">
                  {TIPOS_PREGUNTA.map((t) => (
                    <button key={t.tipo} type="button" className="st-chip" disabled={decir.isPending} onClick={() => decir.mutate({ jobId, fileId: a.fileId, tipo: t.tipo })}>{t.label}</button>
                  ))}
                </div>
              )}
              {a.estado === 'no_se_lee' && a.tipo === 'escandallo' && <button type="button" className="st-enlace" onClick={() => onAbrir('recetas-excel')}>Importar recetas</button>}
              {a.estado === 'no_se_lee' && (a.tipo === 'ventas' || a.tipo === 'ticket_z') && <button type="button" className="st-enlace" onClick={() => onAbrir('tpv')}>Conectar TPV o subir ventas</button>}
              {a.estado === 'error' && <button type="button" className="st-enlace" disabled={reintentar.isPending} onClick={() => reintentar.mutate({ jobId, fileId: a.fileId })}>Reintentar</button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Revisar y guardar                                                    */
/* ------------------------------------------------------------------ */

function GrupoGastos({ jobId, items, hito1 }: { jobId: string; items: Array<ItemGasto & { dudas: string[] }>; hito1: boolean }): JSX.Element {
  const guardar = useGuardarGrupo();
  const toast = useToast();
  const navigate = useNavigate();
  const propuestos = items.filter((i) => i.estado === 'propuesto');
  const dudosos = propuestos.filter((i) => i.dudas.length > 0);
  const claros = propuestos.filter((i) => i.dudas.length === 0);
  const guardados = items.filter((i) => i.estado === 'guardado');
  const alGuardar = async () => {
    try {
      const r = await guardar.mutateAsync({ jobId, grupo: 'gastos' });
      toast.success(`${r.guardados} ${r.guardados === 1 ? 'gasto guardado' : 'gastos guardados'}${r.errores.length ? `; ${r.errores.length} con problemas` : ''}.`);
      if (r.estado.faltas.hito1.cumplido && !hito1) navigate('/resultados');
    } catch (error) { toast.fail(error); }
  };
  return (
    <section className="st-bloque" aria-labelledby="st-gastos">
      <div className="st-bloque__cabecera">
        <h2 id="st-gastos" className="st-h2">Facturas y gastos</h2>
        {claros.length > 0 && (
          <button type="button" className="btn btn--primary" disabled={guardar.isPending} onClick={alGuardar}>
            {guardar.isPending ? 'Guardando…' : `Guardar ${claros.length === 1 ? 'la factura' : `las ${claros.length}`}`}
          </button>
        )}
      </div>
      {dudosos.length > 0 && (
        <div className="st-confirmar">
          <h3 className="st-h3">Para confirmar ({dudosos.length})</h3>
          {dudosos.map((item) => <EditorGasto key={item.itemId} jobId={jobId} item={item} />)}
        </div>
      )}
      {claros.length > 0 && (
        <details className="st-claro">
          <summary>Todo claro ({claros.length})</summary>
          <ul className="st-filas">{claros.map((item) => <FilaGasto key={item.itemId} jobId={jobId} item={item} />)}</ul>
        </details>
      )}
      {guardados.length > 0 && <p className="st-guardados">✓ {guardados.length} {guardados.length === 1 ? 'guardado' : 'guardados'} en <Link to="/gastos">Gastos</Link>.</p>}
    </section>
  );
}

function FilaGasto({ jobId, item }: { jobId: string; item: ItemGasto & { dudas: string[] } }): JSX.Element {
  const editar = useEditarItem();
  const [abierto, setAbierto] = useState(false);
  const p = item.propuesta;
  if (abierto) return <li><EditorGasto jobId={jobId} item={item} onHecho={() => setAbierto(false)} /></li>;
  return (
    <li className="st-fila">
      <div>
        <strong>{p.supplierName ?? p.concept}</strong>
        <span>{formatDate(p.date)} · {CATEGORY_LABELS[p.category]}{item.documentType === 'albaran' ? ' · albarán' : ''}</span>
      </div>
      <span className="st-fila__importe">{formatMoney(totalDeFactura(p))}</span>
      <div className="st-fila__acciones">
        <button type="button" className="st-enlace" onClick={() => setAbierto(true)}>Cambiar</button>
        <button type="button" className="st-enlace st-enlace--suave" disabled={editar.isPending} onClick={() => editar.mutate({ jobId, itemId: item.itemId, cambios: { descartar: true } })}>Quitar</button>
      </div>
    </li>
  );
}

function EditorGasto({ jobId, item, onHecho }: { jobId: string; item: ItemGasto & { dudas: string[] }; onHecho?: () => void }): JSX.Element {
  const editar = useEditarItem();
  const toast = useToast();
  const p = item.propuesta;
  const [proveedor, setProveedor] = useState(p.supplierName ?? p.concept);
  const [fecha, setFecha] = useState(p.date);
  const [base, setBase] = useState(String(p.netAmount).replace('.', ','));
  const [categoria, setCategoria] = useState<ExpenseCategory>(p.category);
  const [tipo, setTipo] = useState(item.documentType);
  const enviar = async () => {
    const neto = parseDecimal(base);
    if (!(neto != null && neto > 0)) { toast.notify('Escribe la base sin IVA.', 'error'); return; }
    try {
      await editar.mutateAsync({ jobId, itemId: item.itemId, cambios: {
        confirmar: true,
        gasto: {
          ...(proveedor !== (p.supplierName ?? p.concept) ? { supplierName: proveedor } : {}),
          date: fecha, netAmount: neto, category: categoria, documentType: tipo,
          ...(p.duplicado ? { noEsRepetida: true } : {}),
        },
      } });
      onHecho?.();
    } catch (error) { toast.fail(error); }
  };
  return (
    <div className="st-editor">
      {item.dudas.length > 0 && <ul className="st-dudas">{item.dudas.map((d) => <li key={d}>{d}</li>)}</ul>}
      <div className="st-editor__campos">
        <label><span>Proveedor</span><input className="input" value={proveedor} onChange={(e) => setProveedor(e.target.value)} /></label>
        <label><span>Fecha</span><input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>
        <label><span>Base sin IVA</span><input className="input" inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} /></label>
        <label><span>Partida</span><SmartSelect value={categoria} onChange={(v) => setCategoria(v as ExpenseCategory)} ariaLabel="Partida" options={CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))} /></label>
        <label><span>Documento</span><SmartSelect value={tipo} onChange={(v) => setTipo(v as 'factura' | 'albaran')} ariaLabel="Tipo de documento" options={[{ value: 'factura', label: 'Factura' }, { value: 'albaran', label: 'Albarán (no cuenta como gasto)' }]} /></label>
      </div>
      <div className="st-editor__botones">
        <button type="button" className="btn btn--sm btn--primary" disabled={editar.isPending} onClick={enviar}>{p.duplicado ? 'No es repetida, está bien' : 'Está bien'}</button>
        <button type="button" className="btn btn--sm btn--ghost" disabled={editar.isPending} onClick={() => editar.mutate({ jobId, itemId: item.itemId, cambios: { descartar: true } })}>Quitar</button>
        {onHecho && <button type="button" className="btn btn--sm btn--ghost" onClick={onHecho}>Cancelar</button>}
      </div>
    </div>
  );
}

function GrupoCarta({ jobId, items }: { jobId: string; items: Array<ItemPlato & { dudas: string[] }> }): JSX.Element {
  const guardar = useGuardarGrupo();
  const toast = useToast();
  const propuestos = items.filter((i) => i.estado === 'propuesto');
  const dudosos = propuestos.filter((i) => i.dudas.length > 0);
  const claros = propuestos.filter((i) => i.dudas.length === 0);
  const guardados = items.filter((i) => i.estado === 'guardado');
  const alGuardar = async () => {
    try {
      const r = await guardar.mutateAsync({ jobId, grupo: 'carta' });
      toast.success(`${r.guardados} ${r.guardados === 1 ? 'plato guardado' : 'platos guardados'} en tu carta.`);
    } catch (error) { toast.fail(error); }
  };
  // Por secciones, como en la carta.
  const secciones = [...new Set(claros.map((p) => p.seccion ?? ''))];
  return (
    <section className="st-bloque" aria-labelledby="st-carta">
      <div className="st-bloque__cabecera">
        <h2 id="st-carta" className="st-h2">Tu carta</h2>
        {claros.length > 0 && (
          <button type="button" className="btn btn--primary" disabled={guardar.isPending} onClick={alGuardar}>
            {guardar.isPending ? 'Guardando…' : `Guardar ${claros.length === 1 ? 'el plato' : `los ${claros.length} platos`}`}
          </button>
        )}
      </div>
      {dudosos.length > 0 && (
        <div className="st-confirmar">
          <h3 className="st-h3">Para confirmar ({dudosos.length})</h3>
          {dudosos.map((item) => <EditorPlato key={item.itemId} jobId={jobId} item={item} />)}
        </div>
      )}
      {claros.length > 0 && (
        <details className="st-claro">
          <summary>Todo claro ({claros.length})</summary>
          {secciones.map((s) => (
            <div key={s || 'sin-seccion'}>
              {s && <p className="st-seccion">{s}</p>}
              <ul className="st-filas">
                {claros.filter((p) => (p.seccion ?? '') === s).map((item) => <FilaPlato key={item.itemId} jobId={jobId} item={item} />)}
              </ul>
            </div>
          ))}
        </details>
      )}
      {guardados.length > 0 && <p className="st-guardados">✓ {guardados.length} {guardados.length === 1 ? 'plato' : 'platos'} en <Link to="/carta">Tu carta</Link>.</p>}
    </section>
  );
}

function FilaPlato({ jobId, item }: { jobId: string; item: ItemPlato & { dudas: string[] } }): JSX.Element {
  const editar = useEditarItem();
  const [abierto, setAbierto] = useState(false);
  if (abierto) return <li><EditorPlato jobId={jobId} item={item} onHecho={() => setAbierto(false)} /></li>;
  return (
    <li className="st-fila">
      <div>
        <strong>{item.nombre.valor}</strong>
        {item.yaEnCarta ? <span>Ya está en tu carta: no la repito</span> : item.descripcion ? <span>{item.descripcion}</span> : null}
      </div>
      <span className="st-fila__importe">{item.precio.valor != null ? formatMoney(item.precio.valor) : '—'}</span>
      <div className="st-fila__acciones">
        <button type="button" className="st-enlace" onClick={() => setAbierto(true)}>Cambiar</button>
        <button type="button" className="st-enlace st-enlace--suave" disabled={editar.isPending} onClick={() => editar.mutate({ jobId, itemId: item.itemId, cambios: { descartar: true } })}>Quitar</button>
      </div>
    </li>
  );
}

function EditorPlato({ jobId, item, onHecho }: { jobId: string; item: ItemPlato & { dudas: string[] }; onHecho?: () => void }): JSX.Element {
  const editar = useEditarItem();
  const toast = useToast();
  const [nombre, setNombre] = useState(item.nombre.valor);
  const [precio, setPrecio] = useState(item.precio.valor != null ? String(item.precio.valor).replace('.', ',') : '');
  const enviar = async () => {
    const pvp = parseDecimal(precio);
    if (!(pvp != null && pvp > 0)) { toast.notify('Escribe el precio de venta (con IVA).', 'error'); return; }
    try {
      await editar.mutateAsync({ jobId, itemId: item.itemId, cambios: { confirmar: true, plato: { nombre, precio: pvp } } });
      onHecho?.();
    } catch (error) { toast.fail(error); }
  };
  return (
    <div className="st-editor">
      {item.dudas.length > 0 && <ul className="st-dudas">{item.dudas.map((d) => <li key={d}>{d}</li>)}</ul>}
      <div className="st-editor__campos st-editor__campos--plato">
        <label><span>Plato</span><input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} /></label>
        <label><span>Precio de venta (con IVA)</span><input className="input" inputMode="decimal" value={precio} placeholder="0,00" onChange={(e) => setPrecio(e.target.value)} /></label>
      </div>
      <div className="st-editor__botones">
        <button type="button" className="btn btn--sm btn--primary" disabled={editar.isPending} onClick={enviar}>Está bien</button>
        <button type="button" className="btn btn--sm btn--ghost" disabled={editar.isPending} onClick={() => editar.mutate({ jobId, itemId: item.itemId, cambios: { descartar: true } })}>No es un plato</button>
        {onHecho && <button type="button" className="btn btn--sm btn--ghost" onClick={onHecho}>Cancelar</button>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lo que falta                                                         */
/* ------------------------------------------------------------------ */

function LoQueFalta({ estado, onAccion }: { estado: EstadoSubirTodo; onAccion: (a: Falta['accion'], afectados?: string[]) => void }): JSX.Element {
  const { faltas, hito1 } = estado.faltas;
  const ayuda = whatsappCon('Hola, estoy poniendo en marcha Alexia con los datos de mi restaurante y me gustaría que me ayudarais.');
  return (
    <section className="st-bloque" aria-labelledby="st-falta">
      <h2 id="st-falta" className="st-h2">{faltas.length ? 'Lo que me falta' : 'No me falta nada'}</h2>
      {hito1.cumplido && (
        <div className="st-listo">
          <strong>Ya tengo lo necesario para tus números.</strong>
          <Link className="btn btn--primary btn--sm" to="/resultados">Ver mi cuenta de resultados</Link>
        </div>
      )}
      {faltas.length > 0 && (
        <ul className="st-faltas">
          {faltas.map((f) => (
            <li key={f.id} className="st-falta">
              <span className={`st-falta__hito st-falta__hito--${f.hito}`}>{f.hito === 1 ? 'Números' : 'Costes'}</span>
              <div className="st-falta__texto">
                <strong>{f.texto}</strong>
                {f.detalle && <span>{f.detalle}</span>}
                {f.afectados?.length ? <span className="st-falta__afectados">{f.afectados.slice(0, 5).join(' · ')}{f.afectados.length > 5 ? '…' : ''}</span> : null}
                <div className="st-falta__botones">
                  <button type="button" className="btn btn--sm" onClick={() => onAccion(f.accion, f.afectados)}>{f.accion.etiqueta}</button>
                  {f.otra && <button type="button" className="btn btn--sm btn--ghost" onClick={() => onAccion(f.otra!, f.afectados)}>{f.otra.etiqueta}</button>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="st-ayuda">
        <strong>¿Prefieres que lo montemos juntos?</strong>
        <span>Escríbenos por WhatsApp y en 20 minutos lo dejamos listo contigo.</span>
        <a className="btn btn--sm" href={ayuda} target="_blank" rel="noreferrer">Escribir por WhatsApp</a>
      </div>
    </section>
  );
}

function LateralTpv({ onClose }: { onClose: () => void }): JSX.Element {
  const { locations } = useLocationScope();
  const me = useMe();
  const tenant = me.data?.tenant;
  return (
    <Drawer open title="Conecta tu TPV" subtitle="Las ventas entrarán solas cada noche." onClose={onClose} width="36rem">
      <PosIntegrationsCard locations={locations} currency={tenant?.currency ?? 'EUR'} country={tenant?.country} />
    </Drawer>
  );
}
